import {otsBaseApi} from "../../ots-api";
import {
  Notice,
  NoticeBadgeSummary,
  NoticeCreateRequestDTO,
  NoticeSearchCriteria,
  NoticeUpdateExpirationRequestDTO
} from "../../../model/notice";
import {Page} from "../../../model/paged-response";

const ALL_TAG_TYPES = [
  "Notices",
  "UserNotices",
  "NoticeBadgeSummary",
  "ObjectObjectChangeLogs",
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

export const noticesApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchNotices: builder.query<Notice[], NoticeSearchCriteria>({
      query: (arg) => ({
        url: `/notices/search`,
        method: "POST",
        body: arg
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Notices", id} as const)),
            {type: "Notices", id: "LIST"}
          ];
        } else {
          return [{type: "Notices", id: "LIST"}];
        }
      }
    }),
    searchUserNotices: builder.query<Page<Notice>, NoticeSearchCriteria>({
      query: (arg) => ({
        url: `/notices/user/search`,
        method: "POST",
        body: arg
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.content.map(({id}) => ({type: "Notices", id} as const)),
            {type: "UserNotices"}
          ];
        } else {
          return [{type: "UserNotices"}];
        }
      }
    }),
    getNotice: builder.query<Notice, string>({
      query: (id) => ({
        url: `/notices/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "Notices", id}]
    }),
    getNoticeBadgeSummary: builder.query<NoticeBadgeSummary, string>({
      query: () => ({
        url: `/notices/badge-summary`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "NoticeBadgeSummary"}]
    }),
    createNotice: builder.mutation<Notice, NoticeCreateRequestDTO>({
      query: (mapping) => ({
        url: "/notices",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [
        {type: "Notices", id: "LIST"},
        {type: "UserNotices"},
        {type: "NoticeBadgeSummary"},
      ]
    }),
    updateNoticeExpiration: builder.mutation<Notice, { id: string, dto: NoticeUpdateExpirationRequestDTO }>({
      query: ({id, dto}) => ({
        url: `/notices/${id}/expiration`,
        method: "PUT",
        body: dto,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Notices", id},
        {type: "ObjectObjectChangeLogs", id},
        {type: "UserNotices"},
        {type: "NoticeBadgeSummary"},
      ]
    }),
    deleteNotice: builder.mutation<void, Partial<Notice>>({
      query: (mapping) => ({
        url: "/notices",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Notices", id},
        {type: "UserNotices"},
        {type: "NoticeBadgeSummary"},
      ]
    }),
  })
});

export const {
  useSearchNoticesQuery,
  useSearchUserNoticesQuery,
  useLazySearchUserNoticesQuery,
  useGetNoticeQuery,
  useGetNoticeBadgeSummaryQuery,
  useCreateNoticeMutation,
  useUpdateNoticeExpirationMutation,
  useDeleteNoticeMutation,
} = noticesApi;
